import React, { createContext, useContext, useEffect, useState } from 'react';
import { theme } from '../../utils/design-system';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme options
export const themes = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

export const ThemeProvider = ({ children }) => {
  // Initialize with the saved theme or default to light
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || themes.LIGHT;
  });

  // Determine if dark mode should be active
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Function to determine if dark mode should be active based on theme setting
    const updateTheme = () => {
      const root = window.document.documentElement;
      const shouldBeDark = theme === themes.DARK || 
        (theme === themes.SYSTEM && window.matchMedia('(prefers-color-scheme: dark)').matches);
      
      setIsDarkMode(shouldBeDark);
      
      if (shouldBeDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    // Update theme immediately
    updateTheme();

    // Listen for system theme changes if in system mode
    if (theme === themes.SYSTEM) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', updateTheme);
      return () => mediaQuery.removeEventListener('change', updateTheme);
    }
  }, [theme]);

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const value = {
    currentTheme: theme,
    isDarkMode,
    setTheme,
    themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 